import { EdgelessLegacySlotIdentifier } from '@blocksuite/affine-block-surface';
import {
  getRenderXYWH,
  getSelectedRect,
  resolveFitToScreenBound,
} from '@blocksuite/affine-shared/utils';
import { deserializeXYWH, type IVec } from '@blocksuite/global/gfx';
import type { PointerEventState } from '@blocksuite/std';
import {
  getTopElements,
  GfxControllerIdentifier,
  type GfxModel,
  InteractivityIdentifier,
  isGfxGroupCompatibleModel,
  type ToolOptionWithType,
} from '@blocksuite/std/gfx';
import { effect } from '@preact/signals-core';

import type { AffineDragHandleWidget } from '../drag-handle.js';
import {
  type EdgelessDragHandleKind,
  type EdgelessDragHandleSlot,
  type EdgelessDragHandleTarget,
  resolveEdgelessDragHandleAnchor,
  resolveEdgelessDragHandleMoveIds,
  resolveEdgelessDragHandleVisibility,
} from '../edgeless-handle-contract.js';

const HANDLE_EXIT_ANIMATION_MS = 140;
/** Owns the two top-level canvas move affordances: selection and direct hover. */
export class EdgelessWatcher {
  private _activeDragKind: EdgelessDragHandleKind | null = null;

  private readonly _clearTimers: Partial<
    Record<EdgelessDragHandleKind, number>
  > = {};

  private _hoveredElementId: string | null = null;

  private _lastPointerClient: IVec | null = null;

  private _toolName: string | null = null;

  private readonly _handleEdgelessToolUpdated = (
    newTool: ToolOptionWithType
  ) => {
    this._toolName = newTool.toolType?.toolName ?? null;
    this._syncHandles();
  };

  private readonly _handleEdgelessViewPortUpdated = ({
    zoom,
    center,
  }: {
    zoom: number;
    center: IVec;
  }) => {
    if (this.widget.scale.peek() !== zoom) {
      this.widget.scale.value = zoom;
    }

    if (
      this.widget.center[0] !== center[0] ||
      this.widget.center[1] !== center[1]
    ) {
      this.widget.center = [...center];
    }

    this._syncHandles();
  };

  private _clearSlot(kind: EdgelessDragHandleKind) {
    const current =
      kind === 'selection'
        ? this.widget.edgelessSelectionHandle
        : this.widget.edgelessHoverHandle;
    this._setSlot(kind, { ...current, visible: false });

    window.clearTimeout(this._clearTimers[kind]);
    this._clearTimers[kind] = window.setTimeout(() => {
      if (this._activeDragKind === kind) return;
      this._setSlot(kind, { target: null, visible: false });
      delete this._clearTimers[kind];
    }, HANDLE_EXIT_ANIMATION_MS);
  }

  private _expandMovingElements(models: readonly GfxModel[]) {
    const moving = new Set<GfxModel>();
    getTopElements([...models]).forEach(model => {
      moving.add(model);
      if (isGfxGroupCompatibleModel(model)) {
        model.descendantElements.forEach(descendant => moving.add(descendant));
      }
    });
    return [...moving];
  }

  private _getHoverTarget(): EdgelessDragHandleTarget | null {
    if (!this._hoveredElementId) return null;
    const element = this.gfx.getElementById<GfxModel>(this._hoveredElementId);
    if (!element || element.isLocked()) return null;
    return this._toTarget('hover', [element]);
  }

  private _getSelectionTarget(): EdgelessDragHandleTarget | null {
    const { selection } = this.gfx;
    const elements = getTopElements(selection.selectedElements);
    if (
      selection.inoperable ||
      elements.length === 0 ||
      elements.some(element => element.isLocked())
    ) {
      return null;
    }
    return this._toTarget('selection', elements);
  }

  private _isPresentation() {
    return this._toolName === 'frameNavigator' || this.gfx.selection.inoperable;
  }

  private _isUnavailable() {
    return (
      this.widget.store.readonly ||
      this.widget.mode === 'page' ||
      this._toolName !== 'default' ||
      this._isPresentation()
    );
  }

  private _setSlot(kind: EdgelessDragHandleKind, slot: EdgelessDragHandleSlot) {
    if (kind === 'selection') {
      this.widget.edgelessSelectionHandle = slot;
    } else {
      this.widget.edgelessHoverHandle = slot;
    }
  }

  private _showSlot(
    kind: EdgelessDragHandleKind,
    target: EdgelessDragHandleTarget
  ) {
    window.clearTimeout(this._clearTimers[kind]);
    delete this._clearTimers[kind];
    this._setSlot(kind, { target, visible: true });
  }

  private readonly _syncHandles = () => {
    if (!this.widget.isConnected || this._isUnavailable()) {
      this._clearSlot('selection');
      this._clearSlot('hover');
      return;
    }

    const selectionTarget = this._getSelectionTarget();
    const hoverTarget = this._getHoverTarget();
    const visibility = resolveEdgelessDragHandleVisibility({
      draggingKind: this._activeDragKind,
      hoverTarget,
      presentation: this._isPresentation(),
      selectionTarget,
    });

    if (visibility.selection && selectionTarget) {
      this._showSlot('selection', selectionTarget);
    } else {
      this._clearSlot('selection');
    }

    if (visibility.hover && hoverTarget) {
      this._showSlot('hover', hoverTarget);
    } else {
      this._clearSlot('hover');
    }
  };

  private _toTarget(
    kind: EdgelessDragHandleKind,
    elements: readonly GfxModel[]
  ): EdgelessDragHandleTarget {
    const rect = getSelectedRect([...elements]);
    const [left, top] = this.gfx.viewport.toViewCoord(rect.left, rect.top);
    const zoom = this.gfx.viewport.zoom;
    const elementIds = elements.map(element => element.id);
    let modelAnchor: { left: number; rotate: number; top: number };
    if (elements.length === 1) {
      const [elementLeft, elementTop, width, height] = deserializeXYWH(
        getRenderXYWH(elements[0])
      );
      modelAnchor = resolveEdgelessDragHandleAnchor({
        height,
        left: elementLeft,
        rotate: elements[0].rotate ?? 0,
        top: elementTop,
        width,
      });
    } else {
      modelAnchor = {
        left: rect.left + rect.width / 2,
        rotate: 0,
        top: rect.top,
      };
    }
    const [anchorLeft, anchorTop] = this.gfx.viewport.toViewCoord(
      modelAnchor.left,
      modelAnchor.top
    );

    return {
      anchor: {
        left: anchorLeft,
        rotate: modelAnchor.rotate,
        top: anchorTop,
      },
      elementIds,
      key: [...elementIds].sort().join('\0'),
      kind,
      rect: {
        height: rect.height * zoom,
        left,
        top,
        width: rect.width * zoom,
      },
    };
  }

  private readonly _updateHoverFromPointer = (state: PointerEventState) => {
    if (this._activeDragKind) return true;

    const raw = state.raw;
    this._lastPointerClient = [raw.x, raw.y];
    if (
      raw.composedPath().some(target => {
        return (
          target instanceof HTMLElement &&
          target.classList.contains('affine-edgeless-drag-handle')
        );
      })
    ) {
      return true;
    }

    if (this._isUnavailable()) {
      this._hoveredElementId = null;
      this._syncHandles();
      return;
    }

    const [x, y] = this.gfx.viewport.toModelCoordFromClientCoord([
      raw.x,
      raw.y,
    ]);
    const hovered = this.gfx.getElementByPoint(x, y);
    const nextId = hovered && !hovered.isLocked() ? hovered.id : null;
    if (nextId !== this._hoveredElementId) {
      this._hoveredElementId = nextId;
      this._syncHandles();
    }
    return;
  };

  get gfx() {
    return this.widget.std.get(GfxControllerIdentifier);
  }

  constructor(readonly widget: AffineDragHandleWidget) {}

  startDrag(kind: EdgelessDragHandleKind, event: PointerEvent) {
    if (
      event.button !== 0 ||
      !event.isPrimary ||
      this._activeDragKind ||
      this._isUnavailable()
    ) {
      return;
    }

    const slot =
      kind === 'selection'
        ? this.widget.edgelessSelectionHandle
        : this.widget.edgelessHoverHandle;
    const target = slot.target;
    if (!target) return;

    const ids = resolveEdgelessDragHandleMoveIds({ kind, target });
    const models = ids
      .map(id => this.gfx.getElementById<GfxModel>(id))
      .filter((model): model is GfxModel => model !== null);
    if (models.length === 0 || models.some(model => model.isLocked())) return;

    event.preventDefault();
    event.stopPropagation();
    this.widget.anchorBlockId.value = null;
    this.widget.activeDragHandle = null;
    this._activeDragKind = kind;

    if (kind === 'hover') {
      this.gfx.selection.set({
        editing: false,
        elements: models.map(model => model.id),
      });
    }
    this._syncHandles();

    const interactivity = this.widget.std.getOptional(InteractivityIdentifier);
    if (!interactivity) {
      this._activeDragKind = null;
      this._syncHandles();
      return;
    }

    this.widget.store.captureSync();
    interactivity.handleElementMove({
      event,
      movingElements: this._expandMovingElements(models),
      onDragEnd: () => {
        this.widget.store.captureSync();
        this._activeDragKind = null;
        this._refreshHoverFromLastPointer();
        this._syncHandles();
      },
    });
  }

  fitTargetToScreen(kind: EdgelessDragHandleKind, event: MouseEvent) {
    if (
      event.button !== 0 ||
      event.shiftKey ||
      event.ctrlKey ||
      event.metaKey ||
      event.altKey ||
      this._isUnavailable()
    ) {
      return;
    }
    const target = (
      kind === 'selection'
        ? this.widget.edgelessSelectionHandle
        : this.widget.edgelessHoverHandle
    ).target;
    if (!target) return;

    event.preventDefault();
    event.stopPropagation();

    const elements = target.elementIds
      .map(id => this.gfx.getElementById<GfxModel>(id))
      .filter((model): model is GfxModel => model !== null);
    if (elements.length === 0) return;
    this.gfx.fitToScreen({
      bounds: elements.map(element =>
        resolveFitToScreenBound(element, this.gfx.view.get(element))
      ),
      padding: [0, 0, 0, 0],
      smooth: true,
    });
  }

  private _refreshHoverFromLastPointer() {
    if (!this._lastPointerClient) return;
    const [x, y] = this.gfx.viewport.toModelCoordFromClientCoord(
      this._lastPointerClient
    );
    this._hoveredElementId = this.gfx.getElementByPoint(x, y)?.id ?? null;
  }

  updateAnchorElement = () => {
    this._syncHandles();
  };

  watch() {
    if (this.widget.mode === 'page') return;

    const { disposables, std } = this.widget;
    const { viewport, selection, tool, surface } = this.gfx;
    const edgelessSlots = std.get(EdgelessLegacySlotIdentifier);

    disposables.add(
      viewport.viewportUpdated.subscribe(this._handleEdgelessViewPortUpdated)
    );
    disposables.add(selection.slots.updated.subscribe(this._syncHandles));
    disposables.add(
      selection.slots.transientGeometryUpdated.subscribe(this._syncHandles)
    );
    disposables.add(edgelessSlots.readonlyUpdated.subscribe(this._syncHandles));
    disposables.add(
      edgelessSlots.elementResizeEnd.subscribe(this._syncHandles)
    );
    disposables.add(
      edgelessSlots.elementResizeStart.subscribe(() => {
        this._clearSlot('selection');
        this._clearSlot('hover');
      })
    );
    disposables.add(
      effect(() => {
        const value = tool.currentToolOption$.value;
        if (value) this._handleEdgelessToolUpdated(value);
      })
    );
    disposables.add(
      std.store.slots.blockUpdated.subscribe(() => this._syncHandles())
    );
    if (surface) {
      disposables.add(surface.elementUpdated.subscribe(this._syncHandles));
      disposables.add(surface.elementRemoved.subscribe(this._syncHandles));
    }
    this.widget.handleEvent('pointerMove', ctx => {
      this._updateHoverFromPointer(ctx.get('pointerState'));
    });
    disposables.add(() => {
      window.clearTimeout(this._clearTimers.hover);
      window.clearTimeout(this._clearTimers.selection);
    });

    this._syncHandles();
  }
}
